import { useState } from 'react';
import { Button, Chip, Select, TextField } from '@nyce/admin-kit/ui';
import type { Link, LinkInput } from '../../api';
import { api } from '../../session';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import {
  EditorDialog,
  EnvChip,
  EnvField,
  IdField,
  SLUG,
  SwitchField,
  errorText,
  useEditorForm,
} from '../../components/registry';
import { ICON_CHOICES, iconFor } from '../LinkedTools';

type Form = Omit<LinkInput, 'launchUrl'> & { id: string; launchUrl: string };

const empty: Form = {
  id: '',
  name: '',
  description: '',
  icon: '',
  environment: 'production',
  url: '',
  launchUrl: '',
  sort: 100,
  enabled: true,
};

const toForm = ({ updatedAt: _u, ...l }: Link): Form => ({
  ...l,
  icon: l.icon ?? '',
  description: l.description ?? '',
  launchUrl: l.launchUrl ?? '',
});

/** Same rule as the API: absolute https, or http on localhost. */
export const isLinkUrl = (u: string) => /^https:\/\/\S+$/.test(u) || /^http:\/\/localhost([:/]\S*)?$/.test(u);

const ICON_OPTIONS = [
  { value: '', label: '(default)' },
  ...ICON_CHOICES.map((i) => ({ value: i, label: i })),
];

function LinkForm({
  initial,
  isNew,
  onSaved,
  onClose,
}: {
  initial: Form;
  isNew: boolean;
  onSaved: (l: Link) => void;
  onClose: () => void;
}) {
  const f = useEditorForm<Form>(initial, isNew);
  const { v, set } = f;
  const urlErr = v.url && !isLinkUrl(v.url) ? 'absolute https:// URL' : undefined;
  const launchErr = v.launchUrl && !isLinkUrl(v.launchUrl) ? 'absolute https:// URL' : undefined;
  const canSave =
    SLUG.test(v.id) &&
    !!v.name.trim() &&
    isLinkUrl(v.url) &&
    (!v.launchUrl || isLinkUrl(v.launchUrl)) &&
    Number.isFinite(v.sort);
  const Icon = iconFor(v.icon);
  const save = () =>
    void f.run(async () => {
      const { id, ...body } = v;
      onSaved(
        await api.admin.links.put(id, {
          ...body,
          name: body.name.trim(),
          launchUrl: body.launchUrl.trim() || null,
        })
      );
    });
  return (
    <EditorDialog
      title={isNew ? 'New link' : `Link ${v.id}`}
      error={f.error}
      busy={f.busy}
      canSave={canSave}
      onSave={save}
      onClose={onClose}
    >
      <TextField label="Name" value={v.name} onChange={(e) => set('name', e.target.value)} required />
      <IdField value={v.id} isNew={isNew} onChange={f.setId} example="buddy-admin" />
      <div className="nyk-span2">
        <TextField
          label="Description"
          value={v.description}
          onChange={(e) => set('description', e.target.value)}
        />
      </div>
      <div className="nyk-span2">
        <TextField
          label="URL"
          className="nyk-url"
          value={v.url}
          onChange={(e) => set('url', e.target.value.trim())}
          error={urlErr}
          placeholder="https://"
          required
        />
      </div>
      <div className="nyk-span2">
        <TextField
          label="Launch URL"
          className="nyk-url"
          value={v.launchUrl}
          onChange={(e) => set('launchUrl', e.target.value.trim())}
          error={launchErr}
          placeholder="optional"
          hint="One-time launch endpoint for pass-through sign-in"
        />
      </div>
      <div className="oph-icon-field">
        <Select label="Icon" value={v.icon} onChange={(i) => set('icon', i)} options={ICON_OPTIONS} />
        <span className="oph-tool-icon" aria-hidden="true" data-testid="icon-preview">
          <Icon size={20} />
        </span>
      </div>
      <TextField
        label="Sort"
        type="number"
        value={String(v.sort)}
        onChange={(e) => set('sort', Number(e.target.value))}
        hint="lower first"
      />
      <EnvField value={v.environment} onChange={(e) => set('environment', e)} />
      <SwitchField label="Enabled" checked={v.enabled} onChange={(e) => set('enabled', e)} />
    </EditorDialog>
  );
}

export function LinksPage() {
  const list = useAsync(() => api.admin.links.list(), []);
  const [editing, setEditing] = useState<{ value: Form; isNew: boolean } | null>(null);
  const notice = useNotice();
  const rows = [...(list.data ?? [])].sort(
    (a, b) =>
      a.environment.localeCompare(b.environment) ||
      a.sort - b.sort ||
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );

  const remove = async (l: Link) => {
    try {
      await api.admin.links.remove(l.id);
      notice.success(`Deleted ${l.name || l.id}`);
      await list.reload();
    } catch (e) {
      notice.error(`Could not delete ${l.name || l.id}: ${errorText(e)}`);
    }
  };

  return (
    <Page
      title="Links"
      description="The tools listed under Linked tools. A launch URL opens the tool already signed in."
      actions={<Button onClick={() => setEditing({ value: { ...empty }, isNew: true })}>Add</Button>}
    >
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      {list.data && !rows.length && <EmptyState card title="No links yet" />}
      {!!rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="links-table">
              <thead>
                <tr>
                  <th>Link</th>
                  <th>Environment</th>
                  <th>URL</th>
                  <th className="nyk-ui-num">Sort</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => {
                  const Icon = iconFor(l.icon);
                  return (
                    <tr key={l.id} data-row={l.id}>
                      <td className="nyk-card-title">
                        <span className="nyk-inline">
                          <Icon size={16} aria-hidden="true" />
                          <b>{l.name || l.id}</b>
                          {l.launchUrl && (
                            <Chip size="sm" className="oph-tool-sso" title="Pass-through sign-in">
                              SSO
                            </Chip>
                          )}
                          {!l.enabled && (
                            <Chip size="sm" className="oph-off">
                              disabled
                            </Chip>
                          )}
                        </span>
                        <div className="nyk-ui-hint">{l.description || l.id}</div>
                      </td>
                      <td data-label="Environment">
                        <EnvChip env={l.environment} />
                      </td>
                      <td data-label="URL" className="nyk-url">
                        {l.url}
                      </td>
                      <td data-label="Sort" className="nyk-ui-num">
                        {l.sort}
                      </td>
                      <td className="nyk-row-actions">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing({ value: toForm(l), isNew: false })}
                        >
                          Edit
                        </Button>
                        <ConfirmButton
                          size="sm"
                          variant="danger"
                          message={`Delete link ${l.name || l.id}?`}
                          onConfirm={() => void remove(l)}
                        >
                          Delete
                        </ConfirmButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {editing && (
        <LinkForm
          initial={editing.value}
          isNew={editing.isNew}
          onClose={() => setEditing(null)}
          onSaved={(l) => {
            setEditing(null);
            notice.success(`Saved ${l.name || l.id}`);
            void list.reload();
          }}
        />
      )}
      {notice.element}
    </Page>
  );
}
